import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { useNavigate } from 'react-router-dom';
import TeamManagementPanel from './TeamManagementPanel';
import UserManagementPanel from './UserManagementPanel';

const AdminConfigPanel = () => {
  const [activeTab, setActiveTab] = useState('notifications');
  const [settings, setSettings] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  // Fetch settings on mount
  useEffect(() => {
    fetchSettings();
    fetchCurrentUser();
  }, []);

  // Update formData when settings change
  useEffect(() => {
    if (settings) {
      setFormData(settings);
    }
  }, [settings]);

  const fetchCurrentUser = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data.user);
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const handleLogout = async () => {
    try {
      // Call backend logout endpoint
      await api.post('/auth/logout', {});
    } catch (error) {
      console.error('Error during logout:', error);
    }

    // Clear token from localStorage
    localStorage.removeItem('token');
    localStorage.removeItem('user');

    // Redirect to login
    navigate('/login');
  };

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/settings');
      setSettings(response.data.data);
      setMessage(null);
    } catch (error) {
      console.error('Error fetching settings:', error);
      setMessage({ type: 'error', text: 'Failed to load settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await api.put('/admin/settings', formData);
      setSettings(response.data.data);
      setMessage({ type: 'success', text: 'Settings saved successfully' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error saving settings:', error);
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Failed to save settings' 
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('Are you sure you want to reset all settings to defaults?')) {
      return;
    }

    try {
      setSaving(true);
      const response = await api.post('/admin/settings/reset', {});
      setSettings(response.data.data);
      setFormData(response.data.data);
      setMessage({ type: 'success', text: 'Settings reset to defaults' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error resetting settings:', error);
      setMessage({ type: 'error', text: 'Failed to reset settings' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData(settings);
    setMessage(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'notifications', label: '🔔 Notifications', icon: '🔔' },
    { id: 'team', label: '👥 Team', icon: '👥' },
    { id: 'users', label: '👤 Users', icon: '👤' },
    { id: 'system', label: '⚙️ System', icon: '⚙️' },
    { id: 'report', label: '📝 Reports', icon: '📝' }
  ];

  return (
    <div className="flex h-screen bg-gray-100">
      {/* Sidebar */}
      <div className="w-48 bg-white shadow-lg">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">⚙️ Settings</h2>
        </div>
        <nav className="p-4 space-y-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-full text-left px-4 py-3 rounded-lg transition ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white font-semibold'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-white shadow p-6 border-b flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">
            {tabs.find(t => t.id === activeTab)?.label}
          </h1>
          
          {/* User Info and Logout */}
          {user && (
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user.full_name}</p>
                <p className="text-xs text-gray-500">{user.email}</p>
              </div>
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
                title="Logout"
              >
                🚪 Logout
              </button>
            </div>
          )}
        </div>

        {/* Message */}
        {message && (
          <div className={`mx-6 mt-4 p-4 rounded-lg ${
            message.type === 'success' 
              ? 'bg-green-100 text-green-800' 
              : 'bg-red-100 text-red-800'
          }`}>
            {message.text}
          </div>
        )}

        {/* Content Area */}
        <div className="flex-1 overflow-auto p-6">
          <div className="bg-white rounded-lg shadow p-6">
            {activeTab === 'notifications' && (
              <NotificationSettings formData={formData} onChange={handleInputChange} />
            )}
            {activeTab === 'team' && (
              <div className="space-y-8">
                <TeamManagementPanel />
                <hr className="my-8" />
                <div>
                  <h3 className="text-lg font-semibold mb-4 text-gray-900">Team Configuration</h3>
                  <TeamConfigSettings formData={formData} onChange={handleInputChange} />
                </div>
              </div>
            )}
            {activeTab === 'users' && (
              <UserManagementPanel />
            )}
            {activeTab === 'system' && (
              <SystemSettings formData={formData} onChange={handleInputChange} />
            )}
            {activeTab === 'report' && (
              <ReportSettings formData={formData} onChange={handleInputChange} />
            )}
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="bg-white border-t p-6 flex justify-end gap-3">
          <button
            onClick={handleCancel}
            disabled={saving}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleReset}
            disabled={saving}
            className="px-4 py-2 border border-yellow-300 rounded-lg text-yellow-700 hover:bg-yellow-50 disabled:opacity-50"
          >
            Reset to Defaults
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Notification Settings Component
const NotificationSettings = ({ formData, onChange }) => (
  <div className="space-y-6">
    <div>
      <h3 className="text-lg font-semibold mb-4 text-gray-900">Reminder Configuration</h3>
      
      <div className="space-y-4">
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            name="reminder_enabled"
            checked={formData.reminder_enabled}
            onChange={onChange}
            className="w-4 h-4"
          />
          <span className="text-gray-700">Enable reminders</span>
        </label>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Reminder Start Time
          </label>
          <input
            type="time"
            name="reminder_start_time"
            value={formData.reminder_start_time}
            onChange={onChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Reminder Interval (minutes)
          </label>
          <input
            type="number"
            name="reminder_interval_minutes"
            value={formData.reminder_interval_minutes}
            onChange={onChange}
            min="5"
            max="120"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          />
          <p className="text-xs text-gray-500 mt-1">Minimum 5 minutes</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Max Retries
          </label>
          <input
            type="number"
            name="reminder_max_retries"
            value={formData.reminder_max_retries}
            onChange={onChange}
            min="1"
            max="10"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          />
        </div>

        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            name="reminder_escalation_enabled"
            checked={formData.reminder_escalation_enabled}
            onChange={onChange}
            className="w-4 h-4"
          />
          <span className="text-gray-700">Enable escalation rules</span>
        </label>

        <label className="flex items-center gap-3 pl-8">
          <input
            type="checkbox"
            name="reminder_escalation_to_admin"
            checked={formData.reminder_escalation_to_admin}
            onChange={onChange}
            disabled={!formData.reminder_escalation_enabled}
            className="w-4 h-4 disabled:opacity-50"
          />
          <span className="text-gray-700">Escalate to admin if not complete</span>
        </label>
      </div>
    </div>
  </div>
);

// Team Configuration Settings Component
const TeamConfigSettings = ({ formData, onChange }) => (
  <div className="space-y-4">
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Report Deadline
      </label>
      <input
        type="time"
        name="team_report_deadline"
        value={formData.team_report_deadline}
        onChange={onChange}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
      />
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Required Reports Per Day
      </label>
      <input
        type="number"
        name="team_required_reports_per_day"
        value={formData.team_required_reports_per_day}
        onChange={onChange}
        min="1"
        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
      />
    </div>

    <label className="flex items-center gap-3">
      <input
        type="checkbox"
        name="team_allow_late_submission"
        checked={formData.team_allow_late_submission}
        onChange={onChange}
        className="w-4 h-4"
      />
      <span className="text-gray-700">Allow late submissions</span>
    </label>
  </div>
);

// System Settings Component
const SystemSettings = ({ formData, onChange }) => (
  <div className="space-y-6">
    <div>
      <h3 className="text-lg font-semibold mb-4 text-gray-900">System Configuration</h3>
      
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Timezone
          </label>
          <select
            name="system_timezone"
            value={formData.system_timezone}
            onChange={onChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="Asia/Ho_Chi_Minh">Vietnam (Ho Chi Minh)</option>
            <option value="UTC">UTC</option>
            <option value="Asia/Bangkok">Asia (Bangkok)</option>
            <option value="America/New_York">America (New York)</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email From Address
          </label>
          <input
            type="email"
            name="system_email_from"
            value={formData.system_email_from}
            onChange={onChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Error Email To
          </label>
          <input
            type="email"
            name="system_error_email_to"
            value={formData.system_error_email_to || ''}
            onChange={onChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            placeholder="Optional"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Logging Level
          </label>
          <select
            name="system_logging_level"
            value={formData.system_logging_level}
            onChange={onChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="debug">Debug</option>
            <option value="info">Info</option>
            <option value="warn">Warning</option>
            <option value="error">Error</option>
          </select>
        </div>

        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            name="system_maintenance_mode"
            checked={formData.system_maintenance_mode}
            onChange={onChange}
            className="w-4 h-4"
          />
          <span className="text-gray-700">Maintenance mode (disable features)</span>
        </label>
      </div>
    </div>
  </div>
);

// Report Settings Component
const ReportSettings = ({ formData, onChange }) => (
  <div className="space-y-6">
    <div>
      <h3 className="text-lg font-semibold mb-4 text-gray-900">Report Configuration</h3>
      
      <div className="space-y-4">
        <label className="flex items-center gap-3">
          <input
            type="checkbox"
            name="report_allow_edit_after_submit"
            checked={formData.report_allow_edit_after_submit}
            onChange={onChange}
            className="w-4 h-4"
          />
          <span className="text-gray-700">Allow editing after submission</span>
        </label>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Auto-Lock Time
          </label>
          <input
            type="time"
            name="report_auto_lock_time"
            value={formData.report_auto_lock_time}
            onChange={onChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          />
          <p className="text-xs text-gray-500 mt-1">Time when reports are automatically locked</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Max Report Length (characters)
          </label>
          <input
            type="number"
            name="report_max_length"
            value={formData.report_max_length}
            onChange={onChange}
            min="100"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          />
        </div>

        <div className="bg-blue-50 p-4 rounded-lg">
          <p className="text-sm text-gray-700">
            <strong>Required Fields:</strong> yesterday_text, today_text
          </p>
          <p className="text-xs text-gray-500 mt-2">
            Currently set in code. Edit config.js to modify.
          </p>
        </div>
      </div>
    </div>
  </div>
);

export default AdminConfigPanel;
